import CodeBlock from '../CodeBlock';

function FnSignature({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-left font-mono text-sm bg-gray-900/50 border border-gray-800 rounded-lg px-4 py-2.5 mb-3 text-gray-300 overflow-x-auto">
      {children}
    </div>
  );
}

function SignalCard({ name, signature, desc, params, example }: {
  name: string;
  signature: string;
  desc: string;
  params: Array<{ name: string; type: string; def?: string; desc: string }>;
  example: string;
}) {
  return (
    <div className="mb-8 text-left">
      <h4 className="text-base font-semibold text-white mb-1">{name}</h4>
      <FnSignature>{signature}</FnSignature>
      <p className="text-gray-400 text-sm mb-2">{desc}</p>
      {params.length > 0 && (
        <div className="space-y-0 mb-2 text-left">
          {params.map((p, i) => (
            <div key={i} className="flex gap-2 text-sm py-1 border-b border-gray-800/50 last:border-0">
              <code className="text-sky-400 shrink-0">{p.name}</code>
              <span className="text-gray-600 shrink-0">{p.type}</span>
              {p.def && <span className="text-gray-600 shrink-0">= {p.def}</span>}
              <span className="text-gray-400">{p.desc}</span>
            </div>
          ))}
        </div>
      )}
      <CodeBlock code={example} />
    </div>
  );
}

export default function DocsSignalsModule() {
  return (
    <section data-docs-section="signals" className="text-left mb-16">
      <h2 className="text-2xl font-bold text-white mb-2 group">
        <a href="#signals" className="hover:text-sky-400 transition-colors">pa.signals</a>
        <span className="ml-2 opacity-0 group-hover:opacity-100 text-gray-600 transition-opacity">#</span>
      </h2>
      <p className="text-gray-400 mb-3">
        Signal generators that accept a prices DataFrame (must have
        a <code className="text-gray-300">close</code> column) and return
        a <code className="text-gray-300">pd.Series</code> of int values
        in <code className="text-gray-300">{'{-1, 0, 1}'}</code>.
      </p>
      <div className="p-3 bg-gray-900/50 border border-gray-800 rounded-lg mb-6 text-sm text-gray-400">
        <strong className="text-gray-300">Signal contract:</strong>{' '}
        <code className="text-green-400">+1</code> = long,{' '}
        <code className="text-gray-300">0</code> = flat,{' '}
        <code className="text-red-400">-1</code> = short. Warmup periods produce 0.
      </div>

      {/* Trend */}
      <h3 id="signals-trend" data-docs-section="signals-trend" className="text-lg font-semibold text-white mb-4 mt-8 group">
        <a href="#signals-trend" className="hover:text-sky-400 transition-colors">Trend</a>
        <span className="ml-2 opacity-0 group-hover:opacity-100 text-gray-600 transition-opacity">#</span>
      </h3>

      <SignalCard
        name="sma_cross()"
        signature="pa.signals.sma_cross(prices, fast=20, slow=100) → Series"
        desc="+1 when the fast SMA is above the slow SMA, -1 when below. Warmup = slow bars."
        params={[
          { name: 'fast', type: 'int', def: '20', desc: 'Fast SMA lookback.' },
          { name: 'slow', type: 'int', def: '100', desc: 'Slow SMA lookback.' },
        ]}
        example={`signal = pa.signals.sma_cross(prices, fast=20, slow=100)`}
      />

      <SignalCard
        name="ema_cross()"
        signature="pa.signals.ema_cross(prices, fast=12, slow=26) → Series"
        desc="+1 when the fast EMA is above the slow EMA, -1 when below. Warmup = slow bars."
        params={[
          { name: 'fast', type: 'int', def: '12', desc: 'Fast EMA span.' },
          { name: 'slow', type: 'int', def: '26', desc: 'Slow EMA span.' },
        ]}
        example={`signal = pa.signals.ema_cross(prices, fast=12, slow=26)`}
      />

      <SignalCard
        name="trend_filter()"
        signature="pa.signals.trend_filter(prices, period=200) → Series"
        desc="+1 when price > SMA, -1 when below. Useful as a regime overlay — multiply another signal by this to suppress counter-trend entries."
        params={[
          { name: 'period', type: 'int', def: '200', desc: 'SMA lookback.' },
        ]}
        example={`regime = pa.signals.trend_filter(prices, period=200)
filtered = signal * regime  # suppress counter-trend entries`}
      />

      {/* Momentum */}
      <h3 id="signals-momentum" data-docs-section="signals-momentum" className="text-lg font-semibold text-white mb-4 mt-8 group">
        <a href="#signals-momentum" className="hover:text-sky-400 transition-colors">Momentum</a>
        <span className="ml-2 opacity-0 group-hover:opacity-100 text-gray-600 transition-opacity">#</span>
      </h3>

      <SignalCard
        name="rsi()"
        signature="pa.signals.rsi(prices, period=14) → Series"
        desc="Raw RSI values in [0, 100] range. Uses Wilder smoothing. Returns raw values, not a discretized signal — use rsi_reversion() for {-1, 0, 1} output."
        params={[
          { name: 'period', type: 'int', def: '14', desc: 'RSI lookback.' },
        ]}
        example={`rsi_values = pa.signals.rsi(prices, period=14)
# Series of float values [0, 100], first 14 values are NaN`}
      />

      <SignalCard
        name="rsi_reversion()"
        signature="pa.signals.rsi_reversion(prices, period=14, overbought=70.0, oversold=30.0) → Series"
        desc="+1 when RSI < oversold (expect bounce), -1 when RSI > overbought (expect pullback), 0 otherwise."
        params={[
          { name: 'period', type: 'int', def: '14', desc: 'RSI lookback.' },
          { name: 'overbought', type: 'float', def: '70.0', desc: 'Upper threshold.' },
          { name: 'oversold', type: 'float', def: '30.0', desc: 'Lower threshold.' },
        ]}
        example={`signal = pa.signals.rsi_reversion(prices, overbought=75, oversold=25)`}
      />

      <SignalCard
        name="macd()"
        signature="pa.signals.macd(prices, fast=12, slow=26, signal_period=9) → Series"
        desc="+1 when MACD line > signal line (bullish), -1 when below (bearish). Warmup = slow + signal_period bars."
        params={[
          { name: 'fast', type: 'int', def: '12', desc: 'Fast EMA span.' },
          { name: 'slow', type: 'int', def: '26', desc: 'Slow EMA span.' },
          { name: 'signal_period', type: 'int', def: '9', desc: 'Signal line EMA span.' },
        ]}
        example={`signal = pa.signals.macd(prices, fast=12, slow=26, signal_period=9)`}
      />

      {/* Mean Reversion */}
      <h3 id="signals-mean-reversion" data-docs-section="signals-mean-reversion" className="text-lg font-semibold text-white mb-4 mt-8 group">
        <a href="#signals-mean-reversion" className="hover:text-sky-400 transition-colors">Mean Reversion</a>
        <span className="ml-2 opacity-0 group-hover:opacity-100 text-gray-600 transition-opacity">#</span>
      </h3>

      <SignalCard
        name="bollinger_bands()"
        signature="pa.signals.bollinger_bands(prices, period=20, num_std=2.0) → Series"
        desc="+1 when close < lower band (expect reversion up), -1 when close > upper band, 0 inside bands."
        params={[
          { name: 'period', type: 'int', def: '20', desc: 'Rolling window.' },
          { name: 'num_std', type: 'float', def: '2.0', desc: 'Band width in standard deviations.' },
        ]}
        example={`signal = pa.signals.bollinger_bands(prices, period=20, num_std=2.0)`}
      />

      <SignalCard
        name="zscore_reversion()"
        signature="pa.signals.zscore_reversion(prices, period=20, entry_z=2.0, exit_z=0.5) → Series"
        desc="+1 when z < -entry_z, -1 when z > +entry_z, 0 when |z| < exit_z. Forward-fills between thresholds (hysteresis)."
        params={[
          { name: 'period', type: 'int', def: '20', desc: 'Rolling window for mean/std.' },
          { name: 'entry_z', type: 'float', def: '2.0', desc: 'Z-score to enter position.' },
          { name: 'exit_z', type: 'float', def: '0.5', desc: 'Z-score to exit position.' },
        ]}
        example={`signal = pa.signals.zscore_reversion(prices, entry_z=2.5, exit_z=0.3)`}
      />

      {/* Combine */}
      <h3 id="signals-combine" data-docs-section="signals-combine" className="text-lg font-semibold text-white mb-4 mt-8 group">
        <a href="#signals-combine" className="hover:text-sky-400 transition-colors">combine()</a>
        <span className="ml-2 opacity-0 group-hover:opacity-100 text-gray-600 transition-opacity">#</span>
      </h3>
      <SignalCard
        name="combine()"
        signature={'pa.signals.combine(signals, method="and") → Series'}
        desc="Combine multiple signal series into a consensus signal."
        params={[
          { name: 'signals', type: 'list[Series]', desc: 'List of signal series.' },
          { name: 'method', type: 'str', def: '"and"', desc: '"and" (unanimous), "or" (any), or "majority" (>50%).' },
        ]}
        example={`s1 = pa.signals.sma_cross(prices, fast=20, slow=100)
s2 = pa.signals.rsi_reversion(prices)
s3 = pa.signals.macd(prices)

# All must agree
consensus = pa.signals.combine([s1, s2, s3], method="and")

# Any signal triggers
any_signal = pa.signals.combine([s1, s2, s3], method="or")

# Majority vote
majority = pa.signals.combine([s1, s2, s3], method="majority")`}
      />

      {/* Multi-Symbol */}
      <h3 id="signals-multi" data-docs-section="signals-multi" className="text-lg font-semibold text-white mb-4 mt-8 group">
        <a href="#signals-multi" className="hover:text-sky-400 transition-colors">Multi-Symbol</a>
        <span className="ml-2 opacity-0 group-hover:opacity-100 text-gray-600 transition-opacity">#</span>
      </h3>

      <SignalCard
        name="rsi_stacked()"
        signature={'pa.signals.rsi_stacked(df, period=14, symbol_col="symbol", close_col="close") → DataFrame'}
        desc="Vectorized RSI across a stacked multi-symbol DataFrame. Appends an rsi column. Uses Wilder smoothing, nulls out symbol boundaries to prevent data leakage. cuDF-compatible."
        params={[
          { name: 'df', type: 'DataFrame', desc: 'Stacked DataFrame, sorted by (symbol, timestamp).' },
          { name: 'period', type: 'int', def: '14', desc: 'RSI lookback.' },
        ]}
        example={`df = pa.data.load("crypto-kraken", interval="1h")
df = pa.signals.rsi_stacked(df, period=14)
# → original DataFrame + "rsi" column (float, 0-100)`}
      />

      <SignalCard
        name="scan()"
        signature={'pa.signals.scan(df, signal_fn, symbol_col="symbol") → DataFrame'}
        desc="Apply any single-symbol signal function across all symbols in a stacked DataFrame. Appends a signal column. Groups that raise exceptions get signal = 0."
        params={[
          { name: 'df', type: 'DataFrame', desc: 'Stacked DataFrame with symbol and close columns.' },
          { name: 'signal_fn', type: '(DataFrame) → Series', desc: 'Signal function for one symbol.' },
        ]}
        example={`df = pa.data.load("crypto-kraken", interval="1d")

# Apply SMA cross to every symbol
df = pa.signals.scan(df, lambda p: pa.signals.sma_cross(p, fast=10, slow=50))
# → original DataFrame + "signal" column`}
      />
    </section>
  );
}
